import { Suspense } from "react"
import { lazyRetry } from "../lib/lazy-retry"
import { Routes, Route, Navigate } from "react-router-dom"
import { DashboardLayout } from "../components/layout/DashboardLayout"
import { SectionLayout } from "../components/layout/SectionLayout"
import {
  MARKETING_TABS,
  COMMUNICATION_TABS,
  AUTOMATION_TABS,
  SYSTEM_TABS,
  COMPANY_TABS,
  DEVELOPER_TABS,
  AI_TABS,
  LAYOUT_TABS,
  BULK_TABS,
  ANALYZE_TABS,
  FINANCES_TABS,
  TRAINING_TABS,
} from "../lib/navigation"
import { RequireAuth } from "../components/system/auth"
import { useUIStore } from "../stores"

// ============================================
// ADMIN DASHBOARD PAGES
// ============================================

// Overview
const Overview = lazyRetry(() => import("../features/overview/pages/OverviewPage"))

// Marketing
const MarketingDaily = lazyRetry(() => import("../features/marketing/pages/DailyPage"))
const MarketingBreakdown = lazyRetry(() => import("../features/marketing/pages/BreakdownPage"))
const MarketingOverview = lazyRetry(() => import("../features/marketing/pages/MarketingOverviewPage"))
const LeadSources = lazyRetry(() => import("../features/marketing/pages/LeadSourcesPage"))
const LeadSourceCategories = lazyRetry(() => import("../features/marketing/pages/LeadSourceCategoriesPage"))
const Leads = lazyRetry(() => import("../features/marketing/pages/LeadsPage"))
const LeadSourceView = lazyRetry(() => import("../features/marketing/pages/LeadSourceViewPage"))
const AIIntake = lazyRetry(() => import("../features/marketing/pages/AIIntakePage"))
const Changelog = lazyRetry(() => import("../features/marketing/pages/ChangelogPage"))

// Analyze
const Analyze = lazyRetry(() => import("../features/analyze/pages/AnalyzePage"))
const CustomReports = lazyRetry(() => import("../features/analyze/pages/CustomReportsPage"))

// Communication
const PhoneNumbers = lazyRetry(() => import("../features/communication/pages/PhoneNumbersPage"))
const CallFlows = lazyRetry(() => import("../features/communication/pages/CallFlowsPage"))
const CallQueues = lazyRetry(() => import("../features/communication/pages/CallQueuesPage"))
const BlockedNumbers = lazyRetry(() => import("../features/communication/pages/BlockedNumbersPage"))
const CallFlowEdit = lazyRetry(() => import("../features/communication/pages/CallFlowEditPage"))

// Automations
const AutomationAssignments = lazyRetry(() => import("../features/automations/pages/AssignmentsPage"))
const EmailSenders = lazyRetry(() => import("../features/automations/pages/EmailSendersPage"))
const AutomationEditor = lazyRetry(() => import("../features/automations/pages/AutomationEditorPage"))
const StateAutomationEditor = lazyRetry(() => import("../features/automations/pages/StateAutomationEditorPage"))
const WorkflowStateEditor = lazyRetry(() => import("../features/automations/pages/WorkflowStateEditorPage"))

const WorkflowEditor = lazyRetry(() => import("../features/system/pages/WorkflowEditorPage"))
const FormEditor = lazyRetry(() => import("../features/system/pages/FormEditorPage"))
const QuestionnaireConfigEditor = lazyRetry(() => import("../features/system/pages/QuestionnaireConfigEditorPage"))

// System
const Workflows = lazyRetry(() => import("../features/system/pages/WorkflowsPage"))
const Forms = lazyRetry(() => import("../features/system/pages/FormsPage"))
const Tags = lazyRetry(() => import("../features/system/pages/TagsPage"))
const Locations = lazyRetry(() => import("../features/system/pages/LocationsPage"))
const Documents = lazyRetry(() => import("../features/system/pages/DocumentsPage"))
const BookingLinks = lazyRetry(() => import("../features/system/pages/BookingLinksPage"))
const EventTemplates = lazyRetry(() => import("../features/system/pages/EventTemplatesPage"))

const CustomFields = lazyRetry(() => import("../features/system/pages/CustomFieldsPage"))
const CustomDates = lazyRetry(() => import("../features/system/pages/CustomDatesPage"))
const ExpectedDates = lazyRetry(() => import("../features/system/pages/ExpectedDatesPage"))
const Departments = lazyRetry(() => import("../features/system/pages/DepartmentsPage"))
const DepartmentDetail = lazyRetry(() => import("../features/system/pages/DepartmentDetailPage"))

// Company
const CompanyUsers = lazyRetry(() => import("../features/staffing/pages/UsersPage"))
const CompanyEdit = lazyRetry(() => import("../features/company/pages/EditPage"))
const CompanyDivisions = lazyRetry(() => import("../features/company/pages/DivisionsPage"))
const StringTemplates = lazyRetry(() => import("../features/company/pages/StringTemplatesPage"))
const CompanyCustomViews = lazyRetry(() => import("../features/company/pages/CustomViewsPage"))

// AI
const AIJobs = lazyRetry(() => import("../features/ai/pages/JobsPage"))
const AIKnowledgePage = lazyRetry(() => import("../features/ai/pages/KnowledgePage"))

// Developers
const ExecutionLogs = lazyRetry(() => import("../features/developers/pages/ExecutionLogsPage"))
const Projections = lazyRetry(() => import("../features/developers/pages/ProjectionsPage"))

// Layout
const LayoutPage = lazyRetry(() => import("../features/layout/pages/LayoutPage"))

// Bulk
const BulkActions = lazyRetry(() => import("../features/bulk/pages/BulkActionsPage"))

// Custom Views
const CustomViews = lazyRetry(() => import("../features/custom-views/pages/CustomViewsPage"))

// Finances
const BillingCategories = lazyRetry(() => import("../features/finances/pages/BillingCategoriesPage"))
const RateCards = lazyRetry(() => import("../features/finances/pages/RateCardsPage"))

// Time Entry Templates (under Finances)
const TimeEntryTemplates = lazyRetry(() => import("../features/system/pages/TimeEntryTemplatesPage"))

// Training
const TrainingCourses = lazyRetry(() => import("../features/training/pages/CoursesPage"))
const TrainingQuizzes = lazyRetry(() => import("../features/training/pages/QuizzesPage"))
const TrainingScenarios = lazyRetry(() => import("../features/training/pages/ScenariosPage"))
const TrainingEnrollments = lazyRetry(() => import("../features/training/pages/EnrollmentsPage"))
const CourseEditor = lazyRetry(() => import("../features/training/pages/CourseEditorPage"))
const QuizEditor = lazyRetry(() => import("../features/training/pages/QuizEditorPage"))
const ScenarioEditor = lazyRetry(() => import("../features/training/pages/ScenarioEditorPage"))

// BK Module

// Errors
const NotFound = lazyRetry(() => import("../features/errors/NotFound"))

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  )
}


function DefaultRoute() {
  const selectedDivision = useUIStore((s) => s.selectedDivision)
  if (!selectedDivision) return <Navigate to="/company" replace />
  return <Overview />
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Dashboard routes wrapped in layout */}
        <Route path="/" element={
          <RequireAuth>
            <DashboardLayout />
          </RequireAuth>
        }>
          {/* ==================== */}
          {/* OVERVIEW             */}
          {/* ==================== */}
          <Route index element={<DefaultRoute />} />
          <Route path="division-users" element={<Navigate to="/" replace />} />

          {/* ==================== */}
          {/* MARKETING            */}
          {/* ==================== */}
          <Route path="marketing" element={<SectionLayout tabs={MARKETING_TABS} />}>
            <Route index element={<MarketingDaily />} />
            <Route path="daily" element={<MarketingDaily />} />
            <Route path="breakdown" element={<MarketingBreakdown />} />
            <Route path="overview" element={<MarketingOverview />} />
            <Route path="lead-sources" element={<LeadSources />} />
            <Route path="lead-source-categories" element={<LeadSourceCategories />} />
            <Route path="lead-sources/:id" element={<LeadSourceView />} />
            <Route path="leads" element={<Leads />} />
            <Route path="ai-intake" element={<AIIntake />} />
            <Route path="changelog" element={<Changelog />} />
          </Route>

          {/* ==================== */}
          {/* ANALYZE              */}
          {/* ==================== */}
          <Route path="analyze" element={<SectionLayout tabs={ANALYZE_TABS} />}>
            <Route index element={<Analyze />} />
            <Route path=":type" element={<CustomReports />} />
            <Route path=":type/:reportId" element={<CustomReports />} />
          </Route>

          {/* ==================== */}
          {/* COMMUNICATE          */}
          {/* ==================== */}
          <Route path="communication" element={<SectionLayout tabs={COMMUNICATION_TABS} />}>
            <Route index element={<Navigate to="phone-numbers" replace />} />
            <Route path="phone-numbers" element={<PhoneNumbers />} />
            <Route path="call-flows" element={<CallFlows />} />
            <Route path="call-flows/:id" element={<CallFlowEdit />} />
            <Route path="call-queues" element={<CallQueues />} />
            <Route path="blocked-numbers" element={<BlockedNumbers />} />
          </Route>

          {/* ==================== */}
          {/* AUTOMATE             */}
          {/* ==================== */}
          <Route path="automations" element={<SectionLayout tabs={AUTOMATION_TABS} />}>
            <Route index element={<AutomationAssignments />} />
            <Route path="assignments" element={<Navigate to="/automations" replace />} />
            <Route path="email-senders" element={<EmailSenders />} />

            {/* Redirects for migrated pages */}
            <Route path="state" element={<Navigate to="/automations" replace />} />
            <Route path="states" element={<Navigate to="/automations" replace />} />
            <Route path="email-templates" element={<Navigate to="/automations" replace />} />
            <Route path="text-templates" element={<Navigate to="/automations" replace />} />
          </Route>

          {/* Editor routes (outside section layout) */}
          <Route path="editor/automations/:id" element={<AutomationEditor />} />
          <Route path="editor/state_automation_templates/:id" element={<StateAutomationEditor />} />
          <Route path="editor/workflow_states/:id" element={<WorkflowStateEditor />} />
          <Route path="editor/workflows/:id/:tab?/:sub?" element={<WorkflowEditor />} />
          <Route path="editor/forms/:id" element={<FormEditor />} />
          <Route path="editor/questionnaire_configs/:id" element={<QuestionnaireConfigEditor />} />

          {/* ==================== */}
          {/* STAFFING (redirects) */}
          {/* ==================== */}
          <Route path="staffing/users" element={<Navigate to="/company/users" replace />} />

          {/* ==================== */}
          {/* SYSTEM               */}
          {/* ==================== */}
          <Route path="system" element={<SectionLayout tabs={SYSTEM_TABS} />}>
            <Route index element={<Navigate to="workflows" replace />} />
            <Route path="workflows" element={<Workflows />} />
            <Route path="forms" element={<Forms />} />
            <Route path="tags" element={<Tags />} />
            <Route path="locations" element={<Locations />} />
            <Route path="location-types" element={<Navigate to="/system/locations" replace />} />
            <Route path="documents" element={<Documents />} />
            <Route path="booking-links" element={<BookingLinks />} />
            <Route path="event-templates" element={<EventTemplates />} />
            <Route path="event-types" element={<Navigate to="/system/event-templates" replace />} />
            <Route path="event-outcomes" element={<Navigate to="/system/event-templates" replace />} />

            <Route path="custom-fields" element={<CustomFields />} />
            <Route path="custom-dates" element={<CustomDates />} />
            <Route path="expected-dates" element={<ExpectedDates />} />
            <Route path="departments" element={<Departments />} />
            <Route path="departments/:id" element={<DepartmentDetail />} />
          </Route>

          {/* ==================== */}
          {/* COMPANY              */}
          {/* ==================== */}
          <Route path="company" element={<SectionLayout tabs={COMPANY_TABS} />}>
            <Route index element={<Navigate to="users" replace />} />
            <Route path="users" element={<CompanyUsers />} />
            <Route path="users/:userId" element={<CompanyUsers />} />
            <Route path="edit" element={<CompanyEdit />} />
            <Route path="divisions" element={<CompanyDivisions />} />
            <Route path="divisions/:divisionId" element={<CompanyDivisions />} />
            <Route path="string-templates" element={<StringTemplates />} />
            <Route path="string-templates/:templateId" element={<StringTemplates />} />
            <Route path="custom-views" element={<CompanyCustomViews />} />
            <Route path="custom-views/:viewId" element={<CompanyCustomViews />} />
          </Route>

          {/* ==================== */}
          {/* AI                   */}
          {/* ==================== */}
          <Route path="ai" element={<SectionLayout tabs={AI_TABS} />}>
            <Route index element={<Navigate to="jobs" replace />} />
            <Route path="jobs" element={<AIJobs />} />
            <Route path="knowledge" element={<AIKnowledgePage />} />
          </Route>


          {/* ==================== */}
          {/* DEVELOPERS           */}
          {/* ==================== */}
          <Route path="developers" element={<SectionLayout tabs={DEVELOPER_TABS} />}>
            <Route index element={<Navigate to="execution-logs" replace />} />
            <Route path="execution-logs" element={<ExecutionLogs />} />
            <Route path="projections" element={<Projections />} />
          </Route>

          {/* ==================== */}
          {/* OTHER PAGES          */}
          {/* ==================== */}
          {/* ==================== */}
          {/* LAYOUT               */}
          {/* ==================== */}
          <Route path="layout" element={<SectionLayout tabs={LAYOUT_TABS} />}>
            <Route index element={<LayoutPage />} />
          </Route>

          {/* ==================== */}
          {/* BULK ACTIONS          */}
          {/* ==================== */}
          <Route path="bulk" element={<SectionLayout tabs={BULK_TABS} />}>
            <Route index element={<BulkActions />} />
          </Route>
          <Route path="custom-views" element={<CustomViews />} />

          {/* ==================== */}
          {/* FINANCES             */}
          {/* ==================== */}
          <Route path="finances" element={<SectionLayout tabs={FINANCES_TABS} />}>
            <Route index element={<BillingCategories />} />
            <Route path="templates" element={<TimeEntryTemplates />} />
            <Route path="rate-cards" element={<RateCards />} />
          </Route>

          {/* ==================== */}
          {/* TRAINING             */}
          {/* ==================== */}
          <Route path="training" element={<SectionLayout tabs={TRAINING_TABS} />}>
            <Route index element={<TrainingCourses />} />
            <Route path="quizzes" element={<TrainingQuizzes />} />
            <Route path="scenarios" element={<TrainingScenarios />} />
            <Route path="enrollments" element={<TrainingEnrollments />} />
          </Route>

          {/* Training editor routes (outside section layout) */}
          <Route path="editor/training_courses/:id" element={<CourseEditor />} />
          <Route path="editor/training_courses/:id/units/:unitId" element={<CourseEditor />} />
          <Route path="editor/training_quizzes/:id" element={<QuizEditor />} />
          <Route path="editor/training_scenarios/:id" element={<ScenarioEditor />} />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
